// Phase 2.10 (hh-1hz) — shared form primitives for the manual-entry screens.
//
// Ported (style-only) from designproject/files/screens-forms.jsx
// (`FormLabel`, `FormInput`, `FormFooter`). Voice is the primary input;
// these primitives back the fallback forms used when voice is not viable.

import type { JSX } from 'preact';
import type { ComponentChildren } from 'preact';
import { T, Btn, type IconName } from '../../tokens';

// ─────────────────────────────────────────────────────────
// FormLabel — uppercase field caption.
// ─────────────────────────────────────────────────────────

export interface FormLabelProps {
  readonly children: ComponentChildren;
  readonly style?: JSX.CSSProperties;
}

export function FormLabel({ children, style }: FormLabelProps): JSX.Element {
  return (
    <div
      style={{
        fontSize: 11.5,
        fontWeight: 700,
        color: T.ink3,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        marginBottom: 6,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// FormInput — single-line or multi-line text field.
// ─────────────────────────────────────────────────────────

export interface FormInputProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly placeholder?: string;
  readonly multi?: boolean;
  readonly disabled?: boolean;
  readonly inputMode?: JSX.HTMLAttributes<HTMLInputElement>['inputMode'];
}

export function FormInput({
  value,
  onChange,
  placeholder,
  multi = false,
  disabled = false,
  inputMode,
}: FormInputProps): JSX.Element {
  const style: JSX.CSSProperties = {
    width: '100%',
    padding: '12px 14px',
    borderRadius: 12,
    border: `1px solid ${T.border}`,
    fontFamily: T.font,
    fontSize: 15,
    color: T.ink,
    background: disabled ? T.surfaceAlt : T.surface,
    outline: 'none',
    boxSizing: 'border-box',
    resize: 'none',
    opacity: disabled ? 0.6 : 1,
  };
  if (multi) {
    return (
      <textarea
        value={value}
        rows={3}
        disabled={disabled}
        placeholder={placeholder}
        onInput={(e) => onChange((e.target as HTMLTextAreaElement).value)}
        style={style}
      />
    );
  }
  return (
    <input
      type="text"
      value={value}
      disabled={disabled}
      placeholder={placeholder}
      inputMode={inputMode}
      onInput={(e) => onChange((e.target as HTMLInputElement).value)}
      style={style}
    />
  );
}

// ─────────────────────────────────────────────────────────
// FormFooter — sticky Cancel / Confirm bar.
// ─────────────────────────────────────────────────────────

export interface FormFooterProps {
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
  readonly confirmLabel: string;
  readonly confirmIcon?: IconName;
  readonly disabled?: boolean;
  readonly pending?: boolean;
}

export function FormFooter({
  onCancel,
  onConfirm,
  confirmLabel,
  confirmIcon,
  disabled = false,
  pending = false,
}: FormFooterProps): JSX.Element {
  return (
    <div
      style={{
        padding: '8px 16px 16px',
        borderTop: `1px solid ${T.border}`,
        background: T.bg,
        display: 'flex',
        gap: 8,
      }}
    >
      <Btn kind="ghost" onClick={onCancel} disabled={pending} style={{ flex: 1 }}>
        Отмена
      </Btn>
      <Btn
        kind="primary"
        {...(confirmIcon !== undefined ? { icon: confirmIcon } : {})}
        onClick={onConfirm}
        disabled={disabled || pending}
        style={{ flex: 1.6 }}
      >
        {pending ? 'Сохраняю…' : confirmLabel}
      </Btn>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// FormError — inline error banner for failed requests.
// ─────────────────────────────────────────────────────────

export interface FormErrorProps {
  readonly message: string;
}

export function FormError({ message }: FormErrorProps): JSX.Element {
  return (
    <div
      style={{
        marginTop: 12,
        padding: '10px 12px',
        background: T.dangerSoft,
        border: '1px solid #E9C7BF',
        borderRadius: 12,
        fontSize: 12.5,
        color: T.danger,
        fontWeight: 600,
      }}
    >
      {message}
    </div>
  );
}
